// TEMPORARY MOCK ADAPTER for PRO Item Design (PRD 04).
// In-memory only — state lives in this module's `domains` and `itemDetails`
// arrays and resets on hard reload. Mutations resolve with ~250 ms simulated
// latency. Errors mimic the backend envelope (ProItemDesignApiError).
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter
// (pro-item-design.adapter.ts).
//
// Seed is translated from prototype/pages/pro-structure.html `_defaultProData`
// with prototype Korean code values mapped to the PRD §3.4 English enums:
//   '배포완료' → 'PUBLISHED'   '임시저장' → 'DRAFT'
//   '매일' → 'DAILY'           '주1회' → 'WEEKLY'
//   '격주' → 'BIWEEKLY'        '월1회' → 'MONTHLY'  '분기별' → 'QUARTERLY'
//   '월'..'일' → 'MON'..'SUN'
//   '퇴원 후 다음날' → DAY_AFTER_DISCHARGE
//   '퇴원 후 다음 주' → WEEK_AFTER_DISCHARGE
//   '퇴원 후 N일' → N_DAYS_AFTER (+ nDays)
//   '공통'/'질병군 공통'/'질병 특화' → COMMON/DISEASE_GROUP/DISEASE_SPECIFIC
//   '=' '≠' '∈' '∉' → 'EQ' 'NEQ' 'IN' (prototype's '∉' not in PRD; mapped to NEQ
//   pending BE confirmation — see docs/api-contracts/pro-item-design.md §6).

import {
  ProItemDesignApiError,
  type ConditionRow,
  type CreateDomainInput,
  type CreateItemInput,
  type CreateSubAreaInput,
  type DayOfWeek,
  type GradeComplexConfig,
  type GradeKey,
  type LinkedQuestion,
  type ProDomain,
  type ProItem,
  type ProItemDesignApi,
  type ProItemHeader,
  type ProSchedule,
  type ProSubArea,
  type ReorderInput,
  type SaveItemInput,
  type UpdateDomainInput,
  type UpdateSubAreaInput,
} from '../types/pro-item-design.types';

const LATENCY_MS = 250;
const ALL_DAYS: DayOfWeek[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── Survey pool (prototype `surveyPool`) ─────────────────────────────────────
// Shared, read-only for the FE: linked questions are sourced from this list
// when assembling each ProItem.linkedQuestions. The Questionnaire Design page
// owns this list in production.
interface SurveyEntry {
  qid: string;
  title: string;
  target: 'PATIENT' | 'GUARDIAN';
  scoreRange: string;
  answers: string[] | null;
  baselineWeight: number;
  baselineConfidence: number;
}

const surveyPool: SurveyEntry[] = [
  { qid: 'Q-0001', title: '최근 1주일 동안 우울감 정도', target: 'PATIENT', scoreRange: '0~3', answers: null, baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0002', title: '본인 스스로를 해치겠다는 생각 여부', target: 'PATIENT', scoreRange: '0~10', answers: ['있다', '없다'], baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0003', title: '과거 자해 시도 여부', target: 'PATIENT', scoreRange: '0~10', answers: ['있다', '없다'], baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0004', title: '자해 방법 구체화 여부', target: 'PATIENT', scoreRange: '0~10', answers: ['있다', '없다'], baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0005', title: '1달 내 자해 실행 가능성', target: 'PATIENT', scoreRange: '0~10', answers: ['전혀 아니다', '약간 그렇다', '매우 그렇다'], baselineWeight: 1.0, baselineConfidence: 0.9 },
  { qid: 'Q-0006', title: '자해 억제 요인 존재 여부', target: 'PATIENT', scoreRange: '0~10', answers: ['있다', '없다'], baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0007', title: '잠들기 어려운 정도', target: 'PATIENT', scoreRange: '0~4', answers: null, baselineWeight: 0.3, baselineConfidence: 0.9 },
  { qid: 'Q-0008', title: '잠 유지 어려움 정도', target: 'PATIENT', scoreRange: '0~4', answers: null, baselineWeight: 0.3, baselineConfidence: 0.9 },
  { qid: 'Q-0009', title: '이른 각성 정도', target: 'PATIENT', scoreRange: '0~4', answers: null, baselineWeight: 0.3, baselineConfidence: 0.9 },
  { qid: 'Q-0010', title: '수면 패턴 만족도', target: 'PATIENT', scoreRange: '0~4', answers: null, baselineWeight: 0.3, baselineConfidence: 0.8 },
  { qid: 'Q-0011', title: '불면으로 인한 삶의 질 손상', target: 'PATIENT', scoreRange: '0~4', answers: null, baselineWeight: 0.3, baselineConfidence: 0.7 },
  { qid: 'Q-0012', title: '불면증에 대한 걱정 정도', target: 'PATIENT', scoreRange: '0~4', answers: null, baselineWeight: 0.3, baselineConfidence: 0.8 },
  { qid: 'Q-0013', title: '수면문제의 일상 방해 정도', target: 'PATIENT', scoreRange: '0~4', answers: null, baselineWeight: 0.3, baselineConfidence: 0.8 },
  { qid: 'Q-0014', title: '식욕 변화 상태', target: 'PATIENT', scoreRange: '0~3', answers: null, baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0015', title: '어제 복약 여부', target: 'PATIENT', scoreRange: '0~10', answers: ['예', '아니오'], baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0016', title: '복약 정도', target: 'PATIENT', scoreRange: '0~10', answers: ['전부', '대부분', '반정도', '조금', '안함'], baselineWeight: 1.0, baselineConfidence: 1.0 },
  { qid: 'Q-0017', title: '미복용 이유', target: 'PATIENT', scoreRange: '0~10', answers: null, baselineWeight: 0.9, baselineConfidence: 1.0 },
];

function findSurvey(qid: string): SurveyEntry | null {
  return surveyPool.find((q) => q.qid === qid) ?? null;
}

// ── Seed: tree ──────────────────────────────────────────────────────────────
// One row per item; the tree shape is reconstructed in setupSeed() so we can
// keep the seed compact and authoritative.
interface SeedItem {
  id: number;
  pid: string;
  name: string;
  description: string;
  scope: ProItem['scope'];
  diseaseGroupId: number | null;
  diseaseCodeId: number | null;
  schedule: ProSchedule;
  linkedQids: string[];
  weights: Record<string, number>;
  confidences: Record<string, number>;
  gradeMethod: ProItem['gradeMethod'];
  gradeSum: ProItem['gradeSum'];
  gradeComplex: GradeComplexConfig | null;
  gradeGuidelines: ProItem['gradeGuidelines'];
  status: ProItem['status'];
}

interface SeedSubArea {
  id: number;
  name: string;
  description: string;
  items: SeedItem[];
}

interface SeedDomain {
  id: number;
  name: string;
  description: string;
  subAreas: SeedSubArea[];
}

const SEED: SeedDomain[] = [
  {
    id: 1,
    name: '정서·인지 증상 관리',
    description: '정서 및 인지 증상의 위험 신호를 선제적으로 모니터링합니다.',
    subAreas: [
      {
        id: 11,
        name: '정서 증상',
        description: '우울감 및 자살 관련 위험을 평가합니다.',
        items: [
          {
            id: 111, pid: 'P-0001', name: '지속적인 우울감',
            description: '지난 1주일 우울감 수준을 간단 척도로 확인합니다.',
            scope: 'COMMON', diseaseGroupId: null, diseaseCodeId: null,
            schedule: { cycle: 'WEEKLY', daysOfWeek: ['MON'], firstExecution: { kind: 'WEEK_AFTER_DISCHARGE' } },
            linkedQids: ['Q-0001'],
            weights: { 'Q-0001': 1.0 }, confidences: { 'Q-0001': 1.0 },
            gradeMethod: 'SUM', gradeSum: { g1Max: 0, g2Max: 1 }, gradeComplex: null,
            gradeGuidelines: { g1: 'G-0007', g2: 'G-0008', g3: 'G-0009' },
            status: 'PUBLISHED',
          },
          {
            id: 112, pid: 'P-0002', name: '자살 관련 행동',
            description: '자살사고 및 실행 위험도를 단계적으로 점검합니다.',
            scope: 'COMMON', diseaseGroupId: null, diseaseCodeId: null,
            schedule: { cycle: 'MONTHLY', daysOfWeek: ['TUE'], firstExecution: { kind: 'WEEK_AFTER_DISCHARGE' } },
            linkedQids: ['Q-0002', 'Q-0003', 'Q-0004', 'Q-0005', 'Q-0006'],
            weights: { 'Q-0002': 1.0, 'Q-0003': 1.0, 'Q-0004': 1.0, 'Q-0005': 1.0, 'Q-0006': 1.0 },
            confidences: { 'Q-0002': 1.0, 'Q-0003': 1.0, 'Q-0004': 1.0, 'Q-0005': 0.9, 'Q-0006': 1.0 },
            gradeMethod: 'SUM', gradeSum: { g1Max: 0, g2Max: 3 }, gradeComplex: null,
            gradeGuidelines: { g1: 'G-0007', g2: 'G-0003', g3: 'G-0006' },
            status: 'PUBLISHED',
          },
        ],
      },
      {
        id: 12,
        name: '약물 관련',
        description: '복약 순응도와 미복용 위험을 평가합니다.',
        items: [
          {
            id: 121, pid: 'P-0003', name: '복약 상태',
            description: '일일 복약 여부와 미복용 이유를 확인합니다.',
            scope: 'COMMON', diseaseGroupId: null, diseaseCodeId: null,
            schedule: { cycle: 'DAILY', daysOfWeek: [...ALL_DAYS], firstExecution: { kind: 'DAY_AFTER_DISCHARGE' } },
            linkedQids: ['Q-0015', 'Q-0016', 'Q-0017'],
            weights: { 'Q-0015': 1.0, 'Q-0016': 1.0, 'Q-0017': 0.9 },
            confidences: { 'Q-0015': 1.0, 'Q-0016': 1.0, 'Q-0017': 1.0 },
            // COMPLEX mode removed (ADM.036 / SHCT-391) — SUM only.
            gradeMethod: 'SUM', gradeSum: { g1Max: 1, g2Max: 3 }, gradeComplex: null,
            gradeGuidelines: { g1: 'G-0001', g2: 'G-0002', g3: 'G-0003' },
            status: 'PUBLISHED',
          },
        ],
      },
      {
        id: 13,
        name: '수면·피로',
        description: '불면 양상과 수면문제로 인한 기능 저하를 확인합니다.',
        items: [
          {
            id: 131, pid: 'P-0004', name: '불면·과수면',
            description: '2주 단위 7문항으로 수면장애 심각도를 평가합니다.',
            scope: 'COMMON', diseaseGroupId: null, diseaseCodeId: null,
            schedule: { cycle: 'BIWEEKLY', daysOfWeek: ['TUE'], firstExecution: { kind: 'WEEK_AFTER_DISCHARGE' } },
            linkedQids: ['Q-0007', 'Q-0008', 'Q-0009', 'Q-0010', 'Q-0011', 'Q-0012', 'Q-0013'],
            weights: { 'Q-0007': 0.3, 'Q-0008': 0.3, 'Q-0009': 0.3, 'Q-0010': 0.3, 'Q-0011': 0.3, 'Q-0012': 0.3, 'Q-0013': 0.3 },
            confidences: { 'Q-0007': 0.9, 'Q-0008': 0.9, 'Q-0009': 0.9, 'Q-0010': 0.8, 'Q-0011': 0.7, 'Q-0012': 0.8, 'Q-0013': 0.8 },
            gradeMethod: 'SUM', gradeSum: { g1Max: 14, g2Max: 21 }, gradeComplex: null,
            gradeGuidelines: { g1: 'G-0004', g2: 'G-0008', g3: 'G-0009' },
            status: 'PUBLISHED',
          },
        ],
      },
    ],
  },
  {
    id: 2,
    name: '신체·생활습관 요인 관리',
    description: '신체 건강 및 생활습관 요인을 관리하는 영역입니다.',
    subAreas: [
      {
        id: 21,
        name: '건강행태',
        description: '신체활동, 음주 등 건강행태 관련 하위영역',
        items: [
          {
            id: 211, pid: 'P-0005', name: '신체활동 악화',
            description: 'IPAQ 기반 신체활동 수준 변화를 측정합니다.',
            scope: 'COMMON', diseaseGroupId: null, diseaseCodeId: null,
            schedule: { cycle: 'MONTHLY', daysOfWeek: ['TUE'], firstExecution: { kind: 'WEEK_AFTER_DISCHARGE' } },
            linkedQids: [], weights: {}, confidences: {},
            gradeMethod: 'SUM', gradeSum: { g1Max: 4, g2Max: 8 }, gradeComplex: null,
            gradeGuidelines: { g1: 'G-0005', g2: 'G-0008', g3: 'G-0009' },
            status: 'PUBLISHED',
          },
          {
            id: 212, pid: 'P-0006', name: '고위험 음주',
            description: 'AUDIT-C 기반 음주 위험도를 평가합니다.',
            scope: 'COMMON', diseaseGroupId: null, diseaseCodeId: null,
            schedule: { cycle: 'MONTHLY', daysOfWeek: ['TUE'], firstExecution: { kind: 'WEEK_AFTER_DISCHARGE' } },
            linkedQids: [], weights: {}, confidences: {},
            gradeMethod: 'SUM', gradeSum: { g1Max: 3, g2Max: 6 }, gradeComplex: null,
            gradeGuidelines: { g1: 'G-0007', g2: 'G-0008', g3: 'G-0009' },
            status: 'PUBLISHED',
          },
        ],
      },
    ],
  },
];

// ── Materialised state ──────────────────────────────────────────────────────
// `domains` keeps the tree structure (matches getTree shape minus ProItemHeader
// scheduleSummary, which is computed on read). `itemDetails` holds the full
// ProItem records keyed by id (returned by getItem). The tree only stores
// header info; mutations update both stores.

interface InternalItem {
  id: number;
  domainId: number;
  subAreaId: number;
  pid: string;
  name: string;
  description: string | null;
  scope: ProItem['scope'];
  diseaseGroupId: number | null;
  diseaseCodeId: number | null;
  schedule: ProSchedule;
  linkedQids: string[];
  weights: Record<string, number>;
  confidences: Record<string, number>;
  gradeMethod: ProItem['gradeMethod'];
  gradeSum: ProItem['gradeSum'];
  gradeComplex: GradeComplexConfig | null;
  gradeGuidelines: ProItem['gradeGuidelines'];
  status: ProItem['status'];
  order: number;
  updatedAt: string;
}

interface InternalSubArea {
  id: number;
  domainId: number;
  name: string;
  description: string | null;
  order: number;
  itemIds: number[];
}

interface InternalDomain {
  id: number;
  name: string;
  description: string | null;
  order: number;
  subAreaIds: number[];
}

const domains: InternalDomain[] = [];
const subAreas = new Map<number, InternalSubArea>();
const items = new Map<number, InternalItem>();

let nextId = 1_000;
let nextPidSerial = 0;

// Frozen seed timestamp — Date.now() is forbidden in this module per harness
// rules. BE owns the real value; the mock just keeps a stable placeholder.
const SEED_UPDATED_AT = '2026-04-30T00:00:00.000Z';

function setupSeed() {
  let domainOrder = 0;
  for (const d of SEED) {
    domains.push({
      id: d.id,
      name: d.name,
      description: d.description || null,
      order: domainOrder++,
      subAreaIds: d.subAreas.map((s) => s.id),
    });
    let subOrder = 0;
    for (const s of d.subAreas) {
      subAreas.set(s.id, {
        id: s.id,
        domainId: d.id,
        name: s.name,
        description: s.description || null,
        order: subOrder++,
        itemIds: s.items.map((i) => i.id),
      });
      let itemOrder = 0;
      for (const i of s.items) {
        items.set(i.id, {
          id: i.id,
          domainId: d.id,
          subAreaId: s.id,
          pid: i.pid,
          name: i.name,
          description: i.description || null,
          scope: i.scope,
          diseaseGroupId: i.diseaseGroupId,
          diseaseCodeId: i.diseaseCodeId,
          schedule: clone(i.schedule),
          linkedQids: i.linkedQids.slice(),
          weights: { ...i.weights },
          confidences: { ...i.confidences },
          gradeMethod: i.gradeMethod,
          gradeSum: i.gradeSum ? { ...i.gradeSum } : null,
          gradeComplex: i.gradeComplex ? clone(i.gradeComplex) : null,
          gradeGuidelines: { ...i.gradeGuidelines },
          status: i.status,
          order: itemOrder++,
          updatedAt: SEED_UPDATED_AT,
        });
        const serialNum = parseInt(i.pid.replace('P-', ''), 10);
        if (serialNum > nextPidSerial) nextPidSerial = serialNum;
        if (i.id >= nextId) nextId = i.id + 1;
      }
      if (s.id >= nextId) nextId = s.id + 1;
    }
    if (d.id >= nextId) nextId = d.id + 1;
  }
}
setupSeed();

// ── Helpers ─────────────────────────────────────────────────────────────────

function nid(): number {
  return ++nextId;
}

function nextPid(): string {
  nextPidSerial++;
  return `P-${String(nextPidSerial).padStart(4, '0')}`;
}

function notFound(message: string): never {
  throw new ProItemDesignApiError('NOT_FOUND', message);
}

function findDomain(id: number): InternalDomain {
  return domains.find((d) => d.id === id) ?? notFound('해당 영역을 찾을 수 없습니다.');
}

function findSubArea(id: number): InternalSubArea {
  const s = subAreas.get(id);
  if (!s) notFound('해당 하위영역을 찾을 수 없습니다.');
  return s;
}

function findItem(id: number): InternalItem {
  const it = items.get(id);
  if (!it) notFound('해당 항목을 찾을 수 없습니다.');
  return it;
}

function validateName(name: string | undefined, max: number): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) {
    throw new ProItemDesignApiError('NAME_REQUIRED', '이름을 입력해주세요.', {
      fields: { name: '이름을 입력해주세요.' },
    });
  }
  if (trimmed.length > max) {
    throw new ProItemDesignApiError('NAME_TOO_LONG', `이름은 ${max}자 이내로 입력해주세요.`, {
      fields: { name: `이름은 ${max}자 이내로 입력해주세요.` },
    });
  }
  return trimmed;
}

function scheduleSummary(s: ProSchedule | null): string | null {
  if (!s) return null;
  if (s.cycle === 'DAILY') return '매일';
  if (s.cycle === 'QUARTERLY') return '분기별';
  const cycleLabel: Record<string, string> = {
    WEEKLY: '주1회',
    BIWEEKLY: '격주',
    MONTHLY: '월1회',
  };
  const label = cycleLabel[s.cycle] ?? s.cycle;
  return `${label}(${s.daysOfWeek.join(',')})`;
}

function toHeader(it: InternalItem): ProItemHeader {
  return {
    id: it.id,
    domainId: it.domainId,
    subAreaId: it.subAreaId,
    pid: it.pid,
    name: it.name,
    status: it.status,
    order: it.order,
    scheduleSummary: scheduleSummary(it.schedule),
  };
}

function toItem(it: InternalItem): ProItem {
  const linkedQuestions: LinkedQuestion[] = it.linkedQids
    .map((qid) => {
      const q = findSurvey(qid);
      if (!q) return null;
      return {
        qid: q.qid,
        title: q.title,
        target: q.target,
        scoreRange: q.scoreRange,
        answers: q.answers,
        weight: it.weights[qid] ?? q.baselineWeight,
        confidence: it.confidences[qid] ?? q.baselineConfidence,
      } satisfies LinkedQuestion;
    })
    .filter((q): q is LinkedQuestion => q !== null);

  return {
    id: it.id,
    domainId: it.domainId,
    subAreaId: it.subAreaId,
    pid: it.pid,
    name: it.name,
    description: it.description,
    status: it.status,
    order: it.order,
    scheduleSummary: scheduleSummary(it.schedule),
    scope: it.scope,
    diseaseGroupId: it.diseaseGroupId,
    diseaseCodeId: it.diseaseCodeId,
    schedule: clone(it.schedule),
    linkedQuestions,
    gradeMethod: it.gradeMethod,
    gradeSum: it.gradeSum ? { ...it.gradeSum } : null,
    gradeComplex: it.gradeComplex ? clone(it.gradeComplex) : null,
    gradeGuidelines: { ...it.gradeGuidelines },
    updatedAt: it.updatedAt,
  };
}

function toSubArea(s: InternalSubArea): ProSubArea {
  return {
    id: s.id,
    domainId: s.domainId,
    name: s.name,
    description: s.description,
    order: s.order,
    items: s.itemIds
      .map((id) => items.get(id))
      .filter((it): it is InternalItem => Boolean(it))
      .sort((a, b) => a.order - b.order)
      .map(toHeader),
  };
}

function toDomain(d: InternalDomain): ProDomain {
  return {
    id: d.id,
    name: d.name,
    description: d.description,
    order: d.order,
    subAreas: d.subAreaIds
      .map((id) => subAreas.get(id))
      .filter((s): s is InternalSubArea => Boolean(s))
      .sort((a, b) => a.order - b.order)
      .map(toSubArea),
  };
}

function ensureRangeMonotonic(input: SaveItemInput) {
  if (input.gradeMethod === 'SUM') {
    if (!input.gradeSum) {
      throw new ProItemDesignApiError('GRADE_RANGE_MISSING', 'Grade 구간을 입력해주세요.');
    }
    if (input.gradeSum.g2Max <= input.gradeSum.g1Max) {
      throw new ProItemDesignApiError(
        'GRADE_RANGE_OVERLAP',
        'Grade 구간이 중첩됩니다. 범위를 다시 확인해주세요.',
      );
    }
  }
}

function ensureWeightConfidenceRanges(input: SaveItemInput) {
  const check = (
    map: Record<string, number> | undefined,
    code: 'WEIGHT_OUT_OF_RANGE' | 'CONFIDENCE_OUT_OF_RANGE',
    label: string,
  ) => {
    if (!map) return;
    for (const v of Object.values(map)) {
      if (!Number.isFinite(v) || v < 0.01 || v > 1.0) {
        throw new ProItemDesignApiError(code, `${label}는 0.01 ~ 1.00 범위로 입력해주세요.`);
      }
    }
  };
  check(input.weights, 'WEIGHT_OUT_OF_RANGE', '가중치');
  check(input.confidences, 'CONFIDENCE_OUT_OF_RANGE', '신뢰도');
}

function ensureNDaysRange(input: SaveItemInput) {
  if (input.schedule.firstExecution.kind === 'N_DAYS_AFTER') {
    const n = input.schedule.firstExecution.nDays;
    if (n === undefined || !Number.isFinite(n) || n < 1 || n > 365) {
      throw new ProItemDesignApiError(
        'N_DAYS_OUT_OF_RANGE',
        '1에서 365 사이로 입력해주세요.',
        { fields: { 'schedule.firstExecution.nDays': '1에서 365 사이로 입력해주세요.' } },
      );
    }
  }
}

function ensurePublishRequirements(input: SaveItemInput) {
  if (!input.schedule.cycle) {
    throw new ProItemDesignApiError('CYCLE_REQUIRED', '주기를 선택해주세요.');
  }
  if (input.schedule.cycle !== 'DAILY' && input.schedule.daysOfWeek.length === 0) {
    throw new ProItemDesignApiError('DAYS_REQUIRED', '요일을 선택해주세요.');
  }
  if (!input.schedule.firstExecution?.kind) {
    throw new ProItemDesignApiError(
      'FIRST_EXECUTION_REQUIRED',
      '첫 시행 기준을 선택해주세요.',
    );
  }
  if (input.scope === 'DISEASE_GROUP' && !input.diseaseGroupId) {
    throw new ProItemDesignApiError('DISEASE_GROUP_REQUIRED', '질병군을 선택해주세요.', {
      fields: { diseaseGroupId: '질병군을 선택해주세요.' },
    });
  }
  if (input.scope === 'DISEASE_SPECIFIC' && !input.diseaseCodeId) {
    throw new ProItemDesignApiError('DISEASE_CODE_REQUIRED', '질병코드를 선택해주세요.', {
      fields: { diseaseCodeId: '질병코드를 선택해주세요.' },
    });
  }
  const grades: GradeKey[] = ['g1', 'g2', 'g3'];
  for (const g of grades) {
    if (!input.gradeGuidelines[g]) {
      throw new ProItemDesignApiError(
        'GRADE_GUIDELINE_REQUIRED',
        `${g.toUpperCase()} 관리지침을 선택해주세요.`,
        { fields: { [`gradeGuidelines.${g}`]: '관리지침을 선택해주세요.' } },
      );
    }
  }
  if (input.gradeMethod === 'COMPLEX') {
    if (!input.gradeComplex) {
      throw new ProItemDesignApiError(
        'GRADE_CONDITION_REQUIRED',
        '각 Grade에 조건을 1개 이상 설정해주세요.',
      );
    }
    for (const g of grades) {
      const rows = input.gradeComplex[g];
      if (!rows || rows.length === 0) {
        throw new ProItemDesignApiError(
          'GRADE_CONDITION_REQUIRED',
          `${g.toUpperCase()} 조건을 1개 이상 설정해주세요.`,
        );
      }
    }
  }
}

function applyItemEdit(target: InternalItem, input: SaveItemInput, status: ProItem['status']) {
  target.name = validateName(input.name, 200);
  target.description = (input.description ?? null) || null;
  target.scope = input.scope;
  target.diseaseGroupId = input.scope === 'DISEASE_GROUP' ? input.diseaseGroupId ?? null : null;
  target.diseaseCodeId = input.scope === 'DISEASE_SPECIFIC' ? input.diseaseCodeId ?? null : null;
  target.schedule = clone(input.schedule);
  if (target.schedule.cycle === 'DAILY') target.schedule.daysOfWeek = [...ALL_DAYS];
  target.gradeMethod = input.gradeMethod;
  target.gradeSum = input.gradeMethod === 'SUM' ? input.gradeSum ?? null : null;
  target.gradeComplex = input.gradeMethod === 'COMPLEX' ? clone(input.gradeComplex ?? null) : null;
  target.gradeGuidelines = { ...input.gradeGuidelines };
  if (input.weights) {
    for (const [qid, v] of Object.entries(input.weights)) target.weights[qid] = v;
  }
  if (input.confidences) {
    for (const [qid, v] of Object.entries(input.confidences)) target.confidences[qid] = v;
  }
  target.status = status;
  // updatedAt is BE-owned; for the mock keep the seed timestamp so determinism
  // is preserved across reloads. The store layer renders relative times.
}

// ── API surface ─────────────────────────────────────────────────────────────

export const proItemDesignMockApi: ProItemDesignApi = {
  async getTree() {
    await delay();
    return domains
      .slice()
      .sort((a, b) => a.order - b.order)
      .map(toDomain);
  },

  async getItem(itemId) {
    await delay();
    return toItem(findItem(itemId));
  },

  async createDomain(input) {
    await delay();
    const name = validateName(input.name, 200);
    const d: InternalDomain = {
      id: nid(),
      name,
      description: input.description ?? null,
      order: domains.length,
      subAreaIds: [],
    };
    domains.push(d);
    return toDomain(d);
  },

  async updateDomain(id, input) {
    await delay();
    const d = findDomain(id);
    if (input.name !== undefined) d.name = validateName(input.name, 200);
    if (input.description !== undefined) d.description = input.description ?? null;
    return toDomain(d);
  },

  async deleteDomain(id) {
    await delay();
    const idx = domains.findIndex((d) => d.id === id);
    if (idx < 0) notFound('해당 영역을 찾을 수 없습니다.');
    const d = domains[idx];
    for (const sId of d.subAreaIds) {
      const s = subAreas.get(sId);
      if (s) for (const iId of s.itemIds) items.delete(iId);
      subAreas.delete(sId);
    }
    domains.splice(idx, 1);
    domains.forEach((dd, i) => (dd.order = i));
  },

  async createSubArea(domainId, input) {
    await delay();
    const d = findDomain(domainId);
    const name = validateName(input.name, 200);
    const s: InternalSubArea = {
      id: nid(),
      domainId,
      name,
      description: input.description ?? null,
      order: d.subAreaIds.length,
      itemIds: [],
    };
    subAreas.set(s.id, s);
    d.subAreaIds.push(s.id);
    return toSubArea(s);
  },

  async updateSubArea(id, input) {
    await delay();
    const s = findSubArea(id);
    if (input.name !== undefined) s.name = validateName(input.name, 200);
    if (input.description !== undefined) s.description = input.description ?? null;
    return toSubArea(s);
  },

  async deleteSubArea(id) {
    await delay();
    const s = findSubArea(id);
    const d = findDomain(s.domainId);
    for (const iId of s.itemIds) items.delete(iId);
    subAreas.delete(id);
    d.subAreaIds = d.subAreaIds.filter((x) => x !== id);
    d.subAreaIds.forEach((sid, i) => {
      const ss = subAreas.get(sid);
      if (ss) ss.order = i;
    });
  },

  async createItem(subAreaId, input) {
    await delay();
    const s = findSubArea(subAreaId);
    const name = validateName(input.name, 200);
    const it: InternalItem = {
      id: nid(),
      domainId: s.domainId,
      subAreaId,
      pid: nextPid(),
      name,
      description: null,
      scope: 'COMMON',
      diseaseGroupId: null,
      diseaseCodeId: null,
      schedule: { cycle: 'WEEKLY', daysOfWeek: [], firstExecution: { kind: 'WEEK_AFTER_DISCHARGE' } },
      linkedQids: [],
      weights: {},
      confidences: {},
      gradeMethod: 'SUM',
      gradeSum: { g1Max: 0, g2Max: 0 },
      gradeComplex: null,
      gradeGuidelines: { g1: null, g2: null, g3: null },
      status: 'DRAFT',
      order: s.itemIds.length,
      updatedAt: SEED_UPDATED_AT,
    };
    items.set(it.id, it);
    s.itemIds.push(it.id);
    return toItem(it);
  },

  async saveDraftItem(id, input) {
    await delay();
    const it = findItem(id);
    ensureNDaysRange(input);
    ensureWeightConfidenceRanges(input);
    ensureRangeMonotonic(input);
    // Draft is intentionally lenient: skip ensurePublishRequirements().
    applyItemEdit(it, input, 'DRAFT');
    return toItem(it);
  },

  async publishItem(id, input) {
    await delay();
    const it = findItem(id);
    ensureNDaysRange(input);
    ensureWeightConfidenceRanges(input);
    ensureRangeMonotonic(input);
    ensurePublishRequirements(input);
    applyItemEdit(it, input, 'PUBLISHED');
    return toItem(it);
  },

  async deleteItem(id) {
    await delay();
    const it = findItem(id);
    const s = findSubArea(it.subAreaId);
    items.delete(id);
    s.itemIds = s.itemIds.filter((x) => x !== id);
    s.itemIds.forEach((iid, i) => {
      const ii = items.get(iid);
      if (ii) ii.order = i;
    });
  },

  async reorder(input: ReorderInput) {
    await delay();
    if (input.kind === 'DOMAIN') {
      const map = new Map(domains.map((d) => [d.id, d]));
      const next: InternalDomain[] = [];
      input.orderedIds.forEach((id, i) => {
        const d = map.get(id);
        if (d) {
          d.order = i;
          next.push(d);
        }
      });
      domains.splice(0, domains.length, ...next);
      return;
    }
    if (input.kind === 'SUB_AREA') {
      if (input.parentId === null) notFound('parentId는 필수입니다.');
      const d = findDomain(input.parentId);
      d.subAreaIds = input.orderedIds.slice();
      d.subAreaIds.forEach((sid, i) => {
        const s = subAreas.get(sid);
        if (s) s.order = i;
      });
      return;
    }
    // ITEM
    if (input.parentId === null) notFound('parentId는 필수입니다.');
    const s = findSubArea(input.parentId);
    s.itemIds = input.orderedIds.slice();
    s.itemIds.forEach((iid, i) => {
      const it = items.get(iid);
      if (it) it.order = i;
    });
  },
};

// Re-export for cross-feature consumers (slice 14: Management Guidelines).
// Keeps the read-only `proItems` / `topics` derivations sourced from a single
// place. Returns the same shape as `getTree()` — callers handle their own
// flattening.
export function _peekSurveyPool(): ReadonlyArray<SurveyEntry> {
  return surveyPool;
}

// Used by ConditionRow type-checking when assembling the complex grade UI.
// Exported as a named const so tests can assert against operator coverage
// without importing through the API surface.
export const _seedCondRowExample: ConditionRow = {
  qid: 'Q-0015',
  op: 'EQ',
  value: '예',
  logic: null,
};
